import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { CheckCircle, XCircle, SlidersHorizontal } from "@phosphor-icons/react";

const STATE_BADGE = {
  current: "bg-emerald-100 text-emerald-800", acknowledged: "bg-emerald-100 text-emerald-800",
  issued: "bg-emerald-100 text-emerald-800", expiring: "bg-amber-300 text-slate-900",
  awaiting_verification: "bg-blue-100 text-blue-800", awaiting_receipt: "bg-amber-300 text-slate-900",
  expired: "bg-red-600 text-white", missing: "bg-red-600 text-white", outstanding: "bg-red-600 text-white",
};

function MultiSelect({ label, options, selected, onToggle, testid }) {
  return (
    <div>
      <p className="font-body text-sm font-semibold text-slate-700 mb-1">{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => (
          <button key={o.id} type="button" data-testid={`${testid}-${o.id}`} onClick={() => onToggle(o.id)}
            className={`px-2.5 h-9 rounded border-2 text-sm font-semibold ${selected.includes(o.id) ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 text-slate-700"}`}>
            {o.name || o.title || o.ref}
          </button>
        ))}
        {options.length === 0 && <span className="text-sm text-slate-400">None configured</span>}
      </div>
    </div>
  );
}

export default function CompliancePage() {
  const { hasPerm } = useAuth();
  const [roles, setRoles] = useState([]);
  const [roleId, setRoleId] = useState("");
  const [assessment, setAssessment] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [ctypes, setCtypes] = useState([]);
  const [ramsList, setRamsList] = useState([]);
  const [ppeTypes, setPpeTypes] = useState([]);
  const [req, setReq] = useState({ required_competencies: [], required_rams: [], required_ppe: [] });

  const loadRoles = () => api.get("/config/role-templates").then((r) => setRoles(r.data)).catch(() => {});
  useEffect(() => {
    loadRoles();
    api.get("/config/competency-types").then((r) => setCtypes(r.data)).catch(() => {});
    api.get("/rams").then((r) => setRamsList(r.data)).catch(() => {});
    api.get("/config/ppe-types").then((r) => setPpeTypes(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (roleId) api.get(`/compliance/assess/${roleId}`).then((r) => setAssessment(r.data)).catch(() => {});
    else setAssessment(null);
  }, [roleId]);

  const openEdit = () => {
    const role = roles.find((r) => r.id === roleId);
    setReq({
      required_competencies: role?.required_competencies || [],
      required_rams: role?.required_rams || [],
      required_ppe: role?.required_ppe || [],
    });
    setEditOpen(true);
  };
  const toggle = (key, id) => setReq((p) => ({ ...p, [key]: p[key].includes(id) ? p[key].filter((x) => x !== id) : [...p[key], id] }));
  const saveReq = async () => {
    try { await api.put(`/config/role-templates/${roleId}`, req); toast.success("Requirements saved"); setEditOpen(false); loadRoles(); api.get(`/compliance/assess/${roleId}`).then((r) => setAssessment(r.data)); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="flex flex-col gap-5">
      <h1 className="font-display text-3xl font-black tracking-tight text-slate-900">Ready for Work — Compliance</h1>
      <div className="flex items-center gap-3 flex-wrap">
        <select data-testid="role-select" className="h-11 border-2 border-slate-300 rounded px-3 min-w-64" value={roleId} onChange={(e) => setRoleId(e.target.value)}>
          <option value="">Select a role profile…</option>
          {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        {roleId && hasPerm("administrator", "training_manager", "supervisor") && (
          <Button data-testid="edit-requirements" onClick={openEdit} variant="outline" className="font-semibold"><SlidersHorizontal size={16} weight="bold" className="mr-1" />Edit Requirements</Button>
        )}
      </div>
      {roles.length === 0 && <p className="text-slate-400">No role profiles yet. Create Role Templates in Admin → Organisation → Role Templates, then define requirements here.</p>}

      {assessment && (
        <>
          <div className="bg-slate-900 text-white rounded-lg p-4 inline-flex items-center gap-3 w-fit">
            <span className="font-display text-3xl font-black">{assessment.compliant_count}/{assessment.total}</span>
            <span className="font-body">staff fully compliant for <b>{assessment.role.name}</b></span>
          </div>
          <div className="flex flex-col gap-3">
            {assessment.results.map((p) => (
              <div key={p.profile_id} data-testid={`compliance-${p.profile_id}`} className={`bg-white border-2 rounded-lg p-4 border-l-8 ${p.compliant ? "border-l-emerald-500 border-slate-200" : "border-l-red-600 border-red-200"}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <span className="font-body font-semibold text-slate-900">{p.name}</span>
                  <span className={`inline-flex items-center gap-1 text-xs font-black uppercase px-2 py-1 rounded ${p.compliant ? "bg-emerald-600 text-white" : "bg-red-600 text-white"}`}>
                    {p.compliant ? <CheckCircle size={14} weight="fill" /> : <XCircle size={14} weight="fill" />}
                    {p.compliant ? "Compliant" : "Not Fully Compliant"}
                  </span>
                </div>
                <div className="grid sm:grid-cols-3 gap-3 mt-3">
                  {["competencies", "rams", "ppe"].map((grp) => (
                    <div key={grp}>
                      <p className="font-body text-xs font-bold uppercase text-slate-400 mb-1">{grp}</p>
                      <div className="flex flex-col gap-1">
                        {p.items[grp].length ? p.items[grp].map((it) => (
                          <div key={it.id} className="flex items-center justify-between gap-2">
                            <span className="font-body text-sm text-slate-700 truncate">{it.name}</span>
                            <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${STATE_BADGE[it.state] || "bg-slate-100 text-slate-500"}`}>{it.state.replace(/_/g, " ")}</span>
                          </div>
                        )) : <span className="text-xs text-slate-400">—</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Requirements</DialogTitle></DialogHeader>
          <MultiSelect label="Required competencies" options={ctypes} selected={req.required_competencies} onToggle={(id) => toggle("required_competencies", id)} testid="req-comp" />
          <MultiSelect label="Required RAMS" options={ramsList} selected={req.required_rams} onToggle={(id) => toggle("required_rams", id)} testid="req-rams" />
          <MultiSelect label="Required PPE" options={ppeTypes} selected={req.required_ppe} onToggle={(id) => toggle("required_ppe", id)} testid="req-ppe" />
          <Button data-testid="save-requirements" onClick={saveReq} className="bg-blue-600 hover:bg-blue-700 font-semibold">Save Requirements</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
